import React, { useState, useEffect } from "react";
import { styled } from "styled-components";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const TestimonialsSection = styled.section`
  padding: 3rem 1rem;
  background: #fff;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2.5rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h2`
  font-family: sans-serif;
  font-size: 2rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 0.75rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 2px;
    background: #e97d7d;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1rem;
  color: #6c757d;
  text-align: center;
  margin-bottom: 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 1.25rem;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  max-width: 800px;
  margin: 0 auto;
`;

const CarouselTrack = styled.div<{ translateX: number }>`
  display: flex;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${(props) => props.translateX}%);
`;

const TestimonialCard = styled.div<{ isActive: boolean }>`
  min-width: 100%;
  padding: 2rem 1.5rem;
  background: #fff;
  position: relative;
  opacity: ${(props) => (props.isActive ? 1 : 0.7)};
  transition: opacity 0.3s ease;

  &::before {
    content: '"';
    position: absolute;
    top: 0.75rem;
    left: 1.5rem;
    font-size: 3rem;
    color: #e97d7d;
    font-family: serif;
    line-height: 1;
    opacity: 0.2;
  }

  @media (max-width: 768px) {
    padding: 1.75rem 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const TestimonialContent = styled.div`
  margin-left: 1.5rem;
  margin-top: 0.75rem;

  @media (max-width: 768px) {
    margin-left: 1.25rem;
  }

  @media (max-width: 480px) {
    margin-left: 1rem;
  }
`;

const TestimonialText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #2c3e50;
  margin-bottom: 1.25rem;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e97d7d, #ff6b6b);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.2rem;
  box-shadow: 0 4px 12px rgba(233, 125, 125, 0.3);

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.25rem 0;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const AuthorTitle = styled.p`
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const Rating = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const Star = styled.span`
  color: #ffd700;
  font-size: 1.1rem;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 480px) {
    margin-top: 1.25rem;
    gap: 0.75rem;
  }
`;

const NavButton = styled.button`
  width: 40px;
  height: 40px;
  border: 2px solid #e97d7d;
  border-radius: 50%;
  background: #fff;
  color: #e97d7d;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: #e97d7d;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(233, 125, 125, 0.3);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
    background: #f5f5f5;
    border-color: #ddd;
    color: #999;
  }

  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;

  @media (max-width: 480px) {
    margin-top: 0.75rem;
  }
`;

const Dot = styled.button<{ isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${(props) => (props.isActive ? "#e97d7d" : "#ddd")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #e97d7d;
    transform: scale(1.2);
  }

  @media (max-width: 480px) {
    width: 10px;
    height: 10px;
  }
`;

const AnimatedCard = styled.div<{ isVisible: boolean; delay: number }>`
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.isVisible
      ? "translateY(0) scale(1)"
      : "translateY(30px) scale(0.95)"};
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: ${(props) => props.delay}ms;
`;

interface Testimonial {
  id: number;
  name: string;
  title: string;
  text: string;
  rating: number;
  avatar: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Sarah Johnson",
    title: "Jewelry Lover",
    text: "I got this necklace for my anniversary and it's absolutely stunning! The craftsmanship is incredible and it arrived beautifully packaged. My husband was so impressed with the quality.",
    rating: 5,
    avatar: "SJ",
  },
  {
    id: 2,
    name: "Michael Chen",
    title: "Pet Owner",
    text: "The custom pet tag I ordered exceeded all my expectations. The engraving is perfect and my dog looks so stylish! The attention to detail is remarkable.",
    rating: 5,
    avatar: "MC",
  },
  {
    id: 3,
    name: "Emily Rodriguez",
    title: "Home Decor Enthusiast",
    text: "These handmade coasters are not just functional but also beautiful art pieces. They've become a conversation starter in my living room. Highly recommend!",
    rating: 5,
    avatar: "ER",
  },
  {
    id: 4,
    name: "David Thompson",
    title: "Gift Buyer",
    text: "I purchased several items as gifts and everyone loved them! The quality is outstanding and the personal touch makes each piece special. Will definitely order again.",
    rating: 5,
    avatar: "DT",
  },
  {
    id: 5,
    name: "Lisa Park",
    title: "Art Collector",
    text: "The resin art piece I bought is absolutely gorgeous. The colors are vibrant and the finish is flawless. It's become the centerpiece of my home office.",
    rating: 5,
    avatar: "LP",
  },
];

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({
    triggerOnce: false,
  });

  useEffect(() => {
    setShouldAnimate(false);
    const timer = setTimeout(() => {
      setShouldAnimate(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
  };

  const translateX = -currentIndex * 100;

  return (
    <TestimonialsSection ref={sectionRef}>
      <Container>
        <AnimatedCard isVisible={shouldAnimate} delay={0}>
          <SectionTitle>Customer Reviews</SectionTitle>
          <SectionSubtitle>
            Hear what our customers have to say about their Craft Bin
            experience.
          </SectionSubtitle>
        </AnimatedCard>

        <AnimatedCard isVisible={shouldAnimate} delay={200}>
          <CarouselContainer>
            <CarouselTrack translateX={translateX}>
              {testimonials.map((testimonial, index) => (
                <TestimonialCard
                  key={testimonial.id}
                  isActive={index === currentIndex}
                >
                  <TestimonialContent>
                    <Rating>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i}>★</Star>
                      ))}
                    </Rating>
                    <TestimonialText>{testimonial.text}</TestimonialText>
                    <TestimonialAuthor>
                      <AuthorAvatar>{testimonial.avatar}</AuthorAvatar>
                      <AuthorInfo>
                        <AuthorName>{testimonial.name}</AuthorName>
                      </AuthorInfo>
                    </TestimonialAuthor>
                  </TestimonialContent>
                </TestimonialCard>
              ))}
            </CarouselTrack>
          </CarouselContainer>

          <NavigationButtons>
            <NavButton onClick={prevTestimonial}>‹</NavButton>
            <NavButton onClick={nextTestimonial}>›</NavButton>
          </NavigationButtons>

          <DotsContainer>
            {testimonials.map((_, index) => (
              <Dot
                key={index}
                isActive={index === currentIndex}
                onClick={() => goToTestimonial(index)}
              />
            ))}
          </DotsContainer>
        </AnimatedCard>
      </Container>
    </TestimonialsSection>
  );
};

export default Testimonials;
